import {
  Box,
  Button,
  Dialog,
  Backdrop,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  DialogActions,
  DialogContent,
  svgIconClasses,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { ProjectGroup } from 'library/types';
import { GridRowId } from '@mui/x-data-grid-premium';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { useCallback, useEffect, useState } from 'react';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';

export type UpdateProjectFormParams = {
  groupId: number | null;
  title: string;
};

const defaultValues: UpdateProjectFormParams = {
  groupId: null,
  title: '',
};

type UpdateProjectDialogProps = {
  open: boolean;
  projectId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

const UpdateProjectDialog = ({ open, projectId, handleClose: closeDialog, refresh }: UpdateProjectDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([]);
  const [loadingProjectGroups, setLoadingProjectGroups] = useState(false);
  const [selectedProjectGroup, setSelectedProjectGroup] = useState<ProjectGroup | null>(null);

  // form validations & form state
  const schema = yup.object().shape({
    title: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    groupId: yup
      .number()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateProjectFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: UpdateProjectFormParams) => {
    const postData = {
      ...data,
      id: projectId as number,
    };

    try {
      setLoading(true);

      const response = await apiService.updateProject(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="projects.updateProjectCompleted"
            defaultMessage="Ürün düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getProjectGroups = async () => {
    try {
      setLoadingProjectGroups(true);

      const response = await apiService.getProjectGroups();

      if (response.isSuccess) {
        setProjectGroups(response.data);
      }
    } catch {
    } finally {
      setLoadingProjectGroups(false);
    }
  };

  const getProjectInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificProject(projectId as number);

      if (response.isSuccess) {
        setValue('groupId', response.data.groupId);
        setValue('title', response.data.title);

        const projectGroup = projectGroups.find((projectGroup) => projectGroup.id === response.data.groupId);

        if (projectGroup) {
          setSelectedProjectGroup(projectGroup);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [projectId, projectGroups, setValue]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedProjectGroup(null);
  };

  // useEffect hooks
  useEffect(() => {
    if (open) {
      getProjectGroups();
    }
  }, [open]);

  useEffect(() => {
    if (open && projectId && projectGroups.length) {
      getProjectInfos();
    }
  }, [open, projectId, projectGroups, getProjectInfos]);

  return (
    <Dialog maxWidth="lg" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="projects.updateProject" defaultMessage="Ürün Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="projects.updateProjectText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili ürün için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                options={projectGroups}
                loading={loadingProjectGroups}
                value={selectedProjectGroup}
                {...renderAutoCompleteLocaleText()}
                onChange={(_, newValue: ProjectGroup | null) => {
                  setSelectedProjectGroup(newValue);

                  setValue('groupId', newValue ? newValue.id : null);
                }}
                getOptionLabel={(option: ProjectGroup) => option.title}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                    label={<FormattedMessage id="projects.groupTitle" defaultMessage="Ürün Grubu" />}
                    error={!!errors.groupId}
                    helperText={errors.groupId?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProjectGroups ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="title"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="projects.title" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default UpdateProjectDialog;
